// AssignPatients.js — Bulk verifier-assignment page.
//
// Lists every patient the admin can see (same source as patient-list)
// and lets them tick a checkbox per row, then pick a verifier from the
// header dropdown and click "Apply" to assign all selected patients in
// one shot.
//
// Flow:
//   1. Page mount → GET /api/users/getVerifierPatientsPercentage (patients)
//                 + GET /api/users/allverifiers (verifier list + their
//                   currently-assigned patients arrays)
//   2. Render table — each row shows current verifier(s) + a checkbox
//   3. User selects rows + picks "Assign to" verifier + chooses mode
//      ("add" keeps existing verifiers, "replace" wipes them first)
//   4. Click "Apply" → POST /api/users/bulkAssignVerifier
//                      { patientIds, verifierIds, mode }
//   5. On success the page refetches both lists so the UI reflects
//      the new assignments without a page reload.
//
// Only Admin (type 1) and Admin Verifier (type 8) can open this page.

import React, { Component } from "react";
import axios from "axios";
import { apiUrl } from "../../config/config";
import SideBar from "../common/SideBar";
import { performLogout } from "../../utils/logout";
import "./AssignPatients.css";

const PAGE_SIZE = 20;

// localStorage key used to persist the quick-assigner notes textarea.
// Survives page reloads and login sessions for the same browser.
const NOTES_LS_KEY = "assignPatients.notes";

// Known distributor tokens — case-insensitive substring match against
// `customerName`. Used to flag patients whose customer doesn't fall
// into a known distributor so the assigner can confirm.
const KNOWN_DISTRIBUTORS = [
  "SLS", "SURAKSHA", "REDKAR", "MYOBEAT", "GLOBAL", "SAIKET", "SCTIMST", "SRKM",
];

function matchDistributor(customerName) {
  if (!customerName) return null;
  var s = String(customerName).toUpperCase();
  for (var i = 0; i < KNOWN_DISTRIBUTORS.length; i++) {
    if (s.indexOf(KNOWN_DISTRIBUTORS[i]) !== -1) return KNOWN_DISTRIBUTORS[i];
  }
  return null;
}

// Detect a pacemaker patient — defensive across schema names.
// In this codebase `hyper_lipidemia` is reused for pacemaker flag
// (see Reporting-page.js / QC-Home.js); also accept the more obvious
// names in case the field has been renamed.
function isPaced(p) {
  if (!p) return false;
  var truthy = function (v) {
    return v === true || v === 1 || v === "1" || v === "yes" || v === "true";
  };
  if (truthy(p.pacemaker))       return true;
  if (truthy(p.is_pacemaker))    return true;
  if (truthy(p.paced))           return true;
  if (truthy(p.hyper_lipidemia)) return true;
  var dt = String(p.deviceType || p.device_type || "").toLowerCase();
  if (dt.indexOf("pacemaker") !== -1 || dt.indexOf("paced") !== -1) return true;
  return false;
}

// Chunk a flat list of lines into pairs of 2, joined `\n`, with a blank
// line between pairs. The last pair carries a single trailing line when
// the input length is odd. Implements the assigner's rule-6 output
// shape (pairs-of-2, blank between sets, no solo middle lines).
function chunkIntoPairs(lines) {
  if (!Array.isArray(lines) || !lines.length) return "";
  var pairs = [];
  for (var i = 0; i < lines.length; i += 2) {
    pairs.push(lines.slice(i, i + 2).join("\n"));
  }
  return pairs.join("\n\n");
}

// Normalize `exp_device_use_days` — some patient docs have it stored in
// hours (24, 48, 72, 144, 168, 336) instead of days. Detect those and
// convert in-memory so the UI shows the right "1d / 2d / 3d" and the
// notes generator uses the right split rule. Does NOT touch the DB.
//
// Returns null when the value is unusable (missing, zero, nonsense).
//
//   value     interpretation              normalized
//   ────────────────────────────────────────────────
//   1-14      already days                value
//   24,48,72  hours, multiple of 24       value / 24
//   168,336   hours, multiple of 24       value / 24  (7d, 14d)
//   17        ambiguous → round-as-hours  Math.round(17/24) → 1 (only if result 1-14)
//   "1"       string '1'                  1
//   0, NaN    no good signal              null
function normalizeMonitoringDays(raw) {
  var n = Number(raw);
  if (isNaN(n) || n <= 0) return null;

  // Already a sensible day value — use it as-is.
  if (n >= 1 && n <= 14) return Math.round(n);

  // Stored in hours but cleanly divisible by 24 — high-confidence convert.
  if (n >= 24 && n % 24 === 0) {
    var asDays = n / 24;
    if (asDays >= 1 && asDays <= 14) return asDays;
  }

  // > 14 and not cleanly divisible — treat as hours best-effort.
  if (n > 14) {
    var rounded = Math.round(n / 24);
    if (rounded >= 1 && rounded <= 14) return rounded;
  }

  return null;
}

// Holter-assignment skill engine.
//
//   SPLITTING (step 2):  1 Day <=20 datasets -> "full"; 21-40 -> halves
//     (exactly 40 = halves); >40 -> 6hr quarters; 2 Day -> 12hr quarters;
//     N Day (N>=3) -> day 1 .. day N+1; unknown days treated as 1 Day.
//   EXCLUSIONS (step 3): running analysis (4), on hold/stuck (5),
//     delivered / report completed (7, published, mailsentime).
//   PAIRING (step 4): two different files per set, blocks in order, no
//     interleaving; priority files pair together first at the top;
//     SCTIMST only pairs with Suraksha/Saiket/SRKM; 7-day files at the
//     bottom; leftovers -> "hold for next batch"; oldest upload first.
//
// buildSkillSets returns { sets, holds, flags, files } or null.
// limitCount (optional) caps how many pending patients enter the batch.
function buildSkillSets(patients, limitCount) {
  if (!patients || !patients.length) return null;

  var pending = patients.filter(function (p) {
    if (!p) return false;
    if (p.report_status === 7) return false;  // delivered / report completed
    if (p.report_status === 4) return false;  // analysis running / ETA
    if (p.report_status === 5) return false;  // on hold / possible stuck
    if (p.published === true)  return false;
    var ms = p.mailsentime;
    if (ms && String(ms).trim() !== "" && String(ms).trim().toUpperCase() !== "NA") return false;
    return true;
  });
  if (!pending.length) return null;

  function uploadTs(p) {
    var t = p.ConfirmupdatedDate || p.updatedDate || p.created_at;
    if (!t) return Number.MAX_SAFE_INTEGER;
    var ms = new Date(t).getTime();
    return isNaN(ms) ? Number.MAX_SAFE_INTEGER : ms;
  }
  pending = pending
    .map(function (p, i) { return { p: p, i: i }; })
    .sort(function (a, b) {
      var ta = uploadTs(a.p); var tb = uploadTs(b.p);
      if (ta === tb) return a.i - b.i;
      return ta - tb;
    })
    .map(function (x) { return x.p; });
  if (limitCount && pending.length > limitCount) pending = pending.slice(0, limitCount);

  function labelsFor(p) {
    var days  = normalizeMonitoringDays(p.exp_device_use_days);
    var count = Number(p.data_count) || 0;
    if (days === 1 || !days) {
      if (count > 0 && count <= 20) return ["full"];
      return count > 40
        ? ["first 6hr", "sec 6hr", "third 6hr", "fourth 6hr"]
        : ["first hlf", "sec hlf"];   // exactly 40 = halves
    }
    if (days === 2) return ["first 12hr", "sec 12hr", "third 12hr", "fourth 12hr"];
    var out = [];
    for (var i = 1; i <= days + 1; i++) out.push("day " + i);
    return out;
  }

  function priorityOf(p) {
    var raw = p.priority != null && p.priority !== ""
      ? p.priority
      : p.deviceInfo && p.deviceInfo.priority;
    var n = Number(raw);
    return isFinite(n) && n > 0 ? n : 4;
  }

  function instOf(p) {
    var s = String(
      (p.customerName || "") + " " + (p.hospital || "") + " " + (p.doctorName || "")
    ).toUpperCase();
    if (s.indexOf("SCTIMST") !== -1) return "SCTIMST";
    if (s.indexOf("SURAKSHA") !== -1 || s.indexOf("SAIKET") !== -1 || s.indexOf("SRKM") !== -1)
      return "PARTNER";
    return null;
  }

  var files = [];
  pending.forEach(function (p) {
    var dev = String(p.device_id || "").trim();
    if (!dev) return;
    var days = normalizeMonitoringDays(p.exp_device_use_days);
    files.push({
      dev: dev,
      blocks: labelsFor(p),
      next: 0,
      paced: isPaced(p),
      prio: priorityOf(p) === 1,
      inst: instOf(p),
      long: !!(days && days >= 7),
      ts: uploadTs(p),
    });
  });
  if (!files.length) return null;

  files.sort(function (a, b) {
    if (a.prio !== b.prio) return a.prio ? -1 : 1;
    if (a.long !== b.long) return a.long ? 1 : -1;
    return a.ts - b.ts;
  });

  function remaining(f) { return f.next < f.blocks.length; }
  function lineFor(f) {
    var l = f.dev + " " + f.blocks[f.next++];
    if (f.paced) l += " (paced)";
    if (f.prio)  l += " [PRIORITY]";
    return l;
  }
  function compatible(a, b) {
    if (a.dev === b.dev) return false;
    if (a.inst === "SCTIMST" && b.inst !== "PARTNER") return false;
    if (b.inst === "SCTIMST" && a.inst !== "PARTNER") return false;
    return true;
  }

  var queue  = files.slice();
  var active = [];
  var sets   = [];
  var holds  = [];
  var flags  = [];

  while (true) {
    while (active.length < 2 && queue.length) {
      if (!active.length) {
        active.push(queue.shift());
      } else {
        var idx = -1;
        for (var q = 0; q < queue.length; q++) {
          if (compatible(active[0], queue[q])) { idx = q; break; }
        }
        if (idx === -1) {
          var lone = active.shift();
          if (lone.inst === "SCTIMST") {
            flags.push("SCTIMST file " + lone.dev +
              " has no Suraksha/Saiket/SRKM partner in this batch - held");
          }
          while (remaining(lone)) holds.push(lineFor(lone));
        } else {
          active.push(queue.splice(idx, 1)[0]);
        }
      }
    }
    if (active.length < 2) break;
    sets.push(lineFor(active[0]) + "\n" + lineFor(active[1]));
    active = active.filter(remaining);
  }
  active.forEach(function (f) {
    while (remaining(f)) holds.push(lineFor(f));
  });

  return { sets: sets, holds: holds, flags: flags, files: files };
}

// Notes bullets (skill step 6).
function buildSkillNoteBullets(r) {
  var notes = [];
  notes.push(
    r.sets.length + " set" + (r.sets.length === 1 ? "" : "s") +
    (r.holds.length
      ? ", " + r.holds.length + " block" + (r.holds.length === 1 ? "" : "s") + " held for next batch"
      : ", no leftovers")
  );
  var prios = r.files.filter(function (f) { return f.prio; });
  if (prios.length) {
    notes.push("priority first: " + prios.map(function (f) { return f.dev; }).join(", "));
  }
  var paced = r.files.filter(function (f) { return f.paced; });
  if (paced.length) {
    notes.push("paced - route to senior verifier: " +
      paced.map(function (f) { return f.dev; }).join(", "));
  }
  for (var fl = 0; fl < r.flags.length; fl++) notes.push(r.flags[fl]);
  return notes;
}

// Trailing holds + notes shared by both output shapes. The "hold for
// next batch:" header comes FIRST so parseDistributedNotes can stop
// collecting ids there (held blocks are never auto-assigned).
function formatSkillTail(r) {
  var tail = "";
  if (r.holds.length) {
    tail += "\n\n\nhold for next batch:\n" + r.holds.join("\n");
  }
  var notes = buildSkillNoteBullets(r);
  tail += "\n\n\nnotes:\n" + notes.map(function (n) { return "- " + n; }).join("\n");
  return tail;
}

// Flat paired list (no verifier headers) — the Google-Sheets paste shape.
function buildNotesFromPatients(patients) {
  var r = buildSkillSets(patients, null);
  if (!r || (!r.sets.length && !r.holds.length)) return "";
  return r.sets.join("\n\n") + formatSkillTail(r);
}

// How many PATIENTS are processed per Build click (total, not per
// verifier). The same patient set is shown to every verifier — each
// verifier handles a different segment-position of those patients.
// E.g. with 2 patients picked and 2 verifiers, V1 gets all "first hlf"s,
// V2 gets all "sec hlf"s. Keeps each verifier's per-round workload
// small while still letting one Holter recording be verified in
// parallel across the team.
const PATIENTS_PER_BUILD = 2;

// Build a verifier-distributed notes paste. The skill pairing engine
// produces the sets; the sets are then dealt round-robin to the selected
// verifiers, each under a header line with the verifier's name:
//
//   <Verifier A name>
//   <set 1 line 1>
//   <set 1 line 2>
//
//   <set 3 line 1>
//   <set 3 line 2>
//
//
//   <Verifier B name>
//   <set 2 line 1>
//   ...
//
// useAllPatients=true (patients ticked in the table) uses every supplied
// patient; otherwise the batch is capped at PATIENTS_PER_BUILD oldest.
// Holds + notes go at the bottom under parse-safe headers so Apply
// never assigns held blocks.
function buildDistributedNotes(patients, selectedAssigners, allVerifiers, useAllPatients) {
  var assigners = Array.isArray(selectedAssigners) ? selectedAssigners : [];
  var verifiers = Array.isArray(allVerifiers)      ? allVerifiers      : [];

  if (!assigners.length) return buildNotesFromPatients(patients);

  var r = buildSkillSets(patients, useAllPatients ? null : PATIENTS_PER_BUILD);
  if (!r || (!r.sets.length && !r.holds.length)) return "";

  var nameById = {};
  for (var v = 0; v < verifiers.length; v++) {
    if (verifiers[v]) nameById[String(verifiers[v]._id)] = verifiers[v].name;
  }

  var perVerifier = assigners.map(function () { return []; });
  r.sets.forEach(function (set, i) {
    perVerifier[i % assigners.length].push(set);
  });

  var blocks = assigners.map(function (vid, idx) {
    var name = nameById[String(vid)] || ("verifier " + (idx + 1));
    if (!perVerifier[idx].length) return name;
    return name + "\n" + perVerifier[idx].join("\n\n");
  });

  return blocks.join("\n\n\n") + formatSkillTail(r);
}

// Parse a verifier-distributed notes paste — text shaped like:
//
//   Murugalakshmi              ← header line: a verifier's name OR
//   246371 first hlf              "verifier 1" / "verifier1" generic
//   246421 sec hlf
//
//   Boby                       ← next section
//   246455 first hlf
//
// Returns an array of { verifierId, verifierName, deviceIds: [] }.
// If NO recognisable header is present in the text, returns [] — the
// caller falls back to the flat-batch flow (extractDeviceIds).
//
// Header recognition:
//   • Case-insensitive exact match against ANY verifier's name in
//     `allVerifiers` (not just the chip-selected ones, so a user
//     can type the name without ticking the pill).
//   • Generic "verifier N" / "verifierN" maps to the Nth entry of
//     `selectedAssigners` (1-based).
function parseDistributedNotes(text, selectedAssigners, allVerifiers) {
  if (!text) return [];
  var verifiers   = Array.isArray(allVerifiers)      ? allVerifiers      : [];
  var assigners   = Array.isArray(selectedAssigners) ? selectedAssigners : [];

  // Lowercased exact-name → { _id, name }
  var nameMap = {};
  for (var i = 0; i < verifiers.length; i++) {
    var v = verifiers[i];
    if (v && v.name) {
      nameMap[String(v.name).toLowerCase().trim()] = { _id: v._id, name: v.name };
    }
  }

  function matchGeneric(line) {
    var m = String(line).toLowerCase().trim().match(/^verifier\s*(\d+)\s*$/);
    if (!m) return null;
    var n   = Number(m[1]);
    if (n < 1 || n > assigners.length) return null;
    var vid = assigners[n - 1];
    var vf  = verifiers.find(function (x) { return String(x._id) === String(vid); });
    return vf ? { _id: vf._id, name: vf.name } : null;
  }

  var lines    = String(text).split(/\r?\n/);
  var sections = [];
  var current  = null;

  for (var k = 0; k < lines.length; k++) {
    var raw  = lines[k];
    var trim = raw.trim();
    if (!trim) continue;

    // Holds + notes sections are informational — never assigned.
    var lower = trim.toLowerCase();
    if (lower.indexOf("hold for next batch") === 0 || lower.indexOf("notes:") === 0) {
      current = null;
      continue;
    }

    var header = nameMap[trim.toLowerCase()] || matchGeneric(trim);
    if (header) {
      current = {
        verifierId:   header._id,
        verifierName: header.name,
        deviceIds:    [],
      };
      sections.push(current);
      continue;
    }
    if (!current) continue;   // device line before any header → ignored
    var matches = trim.match(/\b\d{4,10}\b/g);
    if (matches) {
      for (var m2 = 0; m2 < matches.length; m2++) current.deviceIds.push(matches[m2]);
    }
  }
  return sections;
}

// Pull every plausible device-id (4-10 digit number) out of a free-form
// paste. Ignores annotations like "first hlf", "sec hlf", "day 1" — only
// the numbers matter. Returns a de-duplicated array of strings.
function extractDeviceIds(text) {
  if (!text) return [];
  var matches = String(text).match(/\b\d{4,10}\b/g) || [];
  var seen = {};
  var out  = [];
  for (var i = 0; i < matches.length; i++) {
    if (!seen[matches[i]]) {
      seen[matches[i]] = true;
      out.push(matches[i]);
    }
  }
  return out;
}

// Pager window — same helper shape as SigningHome.js so the UX matches.
function pageWindow(current, total) {
  if (total <= 7) {
    var out = [];
    for (var i = 1; i <= total; i++) out.push(i);
    return out;
  }
  var window = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) { window.add(2); window.add(3); window.add(4); }
  if (current >= total - 2) {
    window.add(total - 1); window.add(total - 2); window.add(total - 3);
  }
  var pages = Array.from(window).filter(function (n) {
    return n >= 1 && n <= total;
  }).sort(function (a, b) { return a - b; });
  var withGaps = [];
  for (var k = 0; k < pages.length; k++) {
    if (k > 0 && pages[k] - pages[k - 1] > 1) withGaps.push("…");
    withGaps.push(pages[k]);
  }
  return withGaps;
}

export default class AssignPatients extends Component {
  state = {
    patients:        [],        // raw patient list from backend
    verifiers:       [],        // [{_id, name, patients: [...]}]
    selected:        new Set(), // patient _ids selected via checkbox
    targetVerifier:  "",        // _id of the verifier in the header dropdown
    mode:            "add",     // "add" or "replace"
    qInput:          "",        // search term
    assignFilter:    "all",     // "all" | "assigned" | "unassigned"
    sortKey:         "confirmDate",  // default: OLDEST confirmed/uploaded first
                                     //   (matches assigner rule-1 — oldest goes first)
                                     // values: "confirmDate" (oldest first, default)
                                     //         "confirmDateNewest" (newest first)
                                     //         "createdAt" (newest registration first)
                                     //         "name" | "id"
    page:            1,
    loading:         false,
    saving:          false,
    error:           "",
    flash:           "",        // green banner after a successful save

    // ── Quick-assigner notes panel state ─────────────────────────────
    notes:           "",        // pasted device-id text — persisted to localStorage
    notesAssigners:  [],        // verifier _ids picked in the notes panel (multi)
    notesPicker:     "",        // current value of the "+ add assigner" dropdown
    notesMode:       "add",     // "add" | "replace"
    notesApplying:   false,
    notesFlash:      "",        // result of the last "Apply notes" click

    lastUpdated:     null,      // timestamp of the last successful load
  };

  // Auto-poll interval (ms). Distributor / Partner page polls every 30s
  // for live recording state — we match that so newly-confirmed patients
  // and verifier reassignments show up without a manual refresh.
  AUTO_REFRESH_MS = 30000;

  componentDidMount() {
    var t = localStorage.getItem("verifiertype");
    if (t !== "1" && t !== "8") {
      this.setState({
        error: "Only Admin or Admin Verifier (type 1 / 8) can access this page.",
      });
      return;
    }
    // On every page load: if the saved textarea has actual content,
    // restore it. Otherwise leave it empty — load() will auto-build the
    // notes from the live patient list once the fetch finishes, so the
    // device IDs always match real patients on this page.
    try {
      var saved = localStorage.getItem(NOTES_LS_KEY);
      if (saved && saved.length) this.setState({ notes: saved });
    } catch (e) { /* localStorage disabled — ignore */ }

    this.load();
    // Start the background poll. Keep the handle so we can clean it up
    // on unmount — otherwise it'd keep firing after the user navigates
    // away and would also keep hitting the API on the wrong page.
    this._poll = setInterval(this.silentLoad, this.AUTO_REFRESH_MS);
  }

  componentWillUnmount() {
    if (this._poll) {
      clearInterval(this._poll);
      this._poll = null;
    }
  }

  load = async (opts) => {
    // opts.silent = true ⇒ background poll. Doesn't toggle the spinner
    // or wipe a transient error, so it never interrupts the user.
    var silent = !!(opts && opts.silent);
    if (!silent) this.setState({ loading: true, error: "" });
    try {
      // Cache-bust each request — browsers + any intermediate proxy
      // will otherwise happily serve the same GET response for minutes,
      // making the page LOOK auto-refreshed (timestamp ticks) while the
      // payload is stale. `_t=Date.now()` guarantees a fresh round-trip.
      // The no-cache header reinforces the same thing at the HTTP layer.
      var ts = Date.now();
      var noCache = { headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" } };
      // allverifiers returns regular verifiers (type 2). To also surface
      // admin verifiers (type 8) in the assigner picker, hit listByType
      // for type 8 separately and merge the two lists. listByType is
      // the same endpoint Users-by-role uses — same query-param style
      // (type / offset / limit), response shape { users: [], total }.
      var [pRes, vRes, aRes] = await Promise.all([
        axios.get(apiUrl + "api/users/getVerifierPatientsPercentage?_t=" + ts, noCache),
        axios.get(apiUrl + "api/users/allverifiers?_t=" + ts, noCache),
        axios.get(
          apiUrl + "api/users/listByType?type=8&offset=0&limit=500&_t=" + ts,
          noCache
        ).catch(function (err) {
          // eslint-disable-next-line no-console
          console.warn(
            "[AssignPatients] listByType?type=8 failed — admin verifiers "
            + "won't appear in the pill row. Status: "
            + (err && err.response && err.response.status)
            + " · message: " + (err && err.message)
          );
          return { data: { users: [] } };
        }),
      ]);

      // Response shape matches patient-list: { sortedPatients: [...] }.
      var data = pRes.data || {};
      var rows = Array.isArray(data.sortedPatients) ? data.sortedPatients
               : Array.isArray(data.patients)       ? data.patients
               : Array.isArray(data)                ? data
               : [];

      // Filter to assignment-relevant rows (assigner rule-3):
      //   • Confirmation set (Yes / No) — mirrors patient-list behaviour
      //   • Exclude not-yet-analyzed (0, 1), in-progress (4), on-hold (5)
      //     and delivered (7). Keeps 2 (Analyzed), 3 (VerificationCompleted),
      //     and 6 (ReportGenerated) — the actionable states.
      //   Statuses: 0=NotAnalyzed, 1=Analyzing, 2=Analyzed,
      //     3=VerificationCompleted, 4=InProgress, 5=OnHold,
      //     6=ReportGenerated, 7=Delivered.
      rows = rows.filter(function (p) {
        if (!p) return false;
        if (!(p.Confirmation === "Yes" || p.Confirmation === "No")) return false;
        var st = Number(p.report_status);
        if (isNaN(st)) return false;
        if (st === 0 || st === 1 || st === 4 || st === 5 || st === 7) return false;
        return true;
      });

      // Rule-5: flag distributor mismatches — customers whose name
      // doesn't match any known distributor. Logged once per load so
      // the assigner can confirm before assigning.
      var distMismatches = rows
        .filter(function (p) { return p.customerName && !matchDistributor(p.customerName); })
        .map(function (p) { return { device_id: p.device_id, customer: p.customerName }; });
      if (distMismatches.length) {
        // eslint-disable-next-line no-console
        console.warn(
          "[AssignPatients] " + distMismatches.length
          + " patient(s) with non-standard customer — confirm distributor before assigning:",
          distMismatches.slice(0, 10)
        );
      }

      // Rule-5: pacemaker patients should route to a senior verifier.
      // Surface them in the console so the assigner can spot them at a
      // glance even before scrolling the table.
      var pacedRows = rows.filter(function (p) { return isPaced(p); });
      if (pacedRows.length) {
        // eslint-disable-next-line no-console
        console.warn(
          "[AssignPatients] " + pacedRows.length
          + " pacemaker patient(s) on this page — route to senior verifier:",
          pacedRows.slice(0, 10).map(function (p) {
            return { device_id: p.device_id, name: p.name };
          })
        );
      }

      var regular = Array.isArray(vRes.data) ? vRes.data : [];

      // listByType can return either an array OR { users: [], total }.
      // Normalise both, then merge regular + admin verifiers (dedup by _id).
      var adminPayload = aRes && aRes.data ? aRes.data : [];
      var adminList   = Array.isArray(adminPayload)        ? adminPayload
                      : Array.isArray(adminPayload.users)  ? adminPayload.users
                      : Array.isArray(adminPayload.docs)   ? adminPayload.docs
                      : [];
      var seen      = {};
      var verifiers = [];
      regular.concat(adminList).forEach(function (u) {
        if (u && u._id && !seen[String(u._id)]) {
          seen[String(u._id)] = true;
          verifiers.push(u);
        }
      });

      // eslint-disable-next-line no-console
      console.log(
        "[AssignPatients] verifier merge — regular(type 2): " + regular.length
        + " · admin(type 8): " + adminList.length
        + " · merged unique: " + verifiers.length
        + " · admin response keys: "
        + (adminPayload && typeof adminPayload === "object" && !Array.isArray(adminPayload)
            ? Object.keys(adminPayload).join(",")
            : (Array.isArray(adminPayload) ? "(bare array)" : "(none)"))
      );

      // Drop checkbox selections whose patient no longer exists in the
      // fresh fetch — keeps the "selected count" honest.
      var idSet = new Set(rows.map(function (p) { return String(p._id); }));
      var prunedSel = new Set();
      this.state.selected.forEach(function (id) {
        if (idSet.has(id)) prunedSel.add(id);
      });

      // Diagnostic — confirms each poll genuinely reaches the backend
      // and tells you whether the patient count is moving or stuck.
      // eslint-disable-next-line no-console
      console.log("[AssignPatients] " + (silent ? "poll" : "load")
        + " ← patients: " + rows.length
        + " · verifiers: " + verifiers.length
        + " · " + new Date().toLocaleTimeString());

      this.setState({
        patients:    rows,
        verifiers:   verifiers,
        loading:     false,
        selected:    prunedSel,
        lastUpdated: new Date(),
      }, () => {
        // First-time auto-fill — once per page mount. If the textarea
        // is still empty (no saved notes from a prior session) and we
        // now have patients in hand, build the notes from those exact
        // device IDs. Subsequent silent polls don't re-fill so the
        // admin's edits aren't overwritten.
        if (
          !this._notesAutoFilled
          && !this.state.notes
          && rows.length
        ) {
          this._notesAutoFilled = true;
          var generated = buildNotesFromPatients(rows);
          if (generated) {
            this.setState({ notes: generated });
            try { localStorage.setItem(NOTES_LS_KEY, generated); }
            catch (e) { /* ignore */ }
          }
        }
      });
    } catch (err) {
      // Silent polls swallow errors — they shouldn't yank the user out
      // of a working state because the network blipped for one tick.
      if (silent) {
        // eslint-disable-next-line no-console
        console.warn("[AssignPatients] silent poll failed:", err.message);
        return;
      }
      // eslint-disable-next-line no-console
      console.error("[AssignPatients] load error:", err);
      this.setState({
        loading: false,
        error:
          (err.response && err.response.data && err.response.data.error) ||
          err.message || "Failed to load",
      });
    }
  };

  // Wrapper used by the setInterval poll — fires load in silent mode.
  silentLoad = () => { this.load({ silent: true }); };

  // Build a Map<patientId, [{ _id, name }]> of verifiers currently
  // assigned to each patient. Pulled from each verifier's `patients`
  // array (the canonical source on the user doc).
  verifiersByPatient() {
    var map = {};
    var vs = this.state.verifiers || [];
    for (var i = 0; i < vs.length; i++) {
      var v = vs[i];
      var list = Array.isArray(v.patients) ? v.patients : [];
      for (var j = 0; j < list.length; j++) {
        var pid = String(list[j]);
        if (!map[pid]) map[pid] = [];
        // Dedup per patient — guards against the admin-verifier merge
        // accidentally appending the same verifier twice when the same
        // _id appears in both /allverifiers and listByType?type=8.
        var alreadyIn = false;
        for (var k = 0; k < map[pid].length; k++) {
          if (String(map[pid][k]._id) === String(v._id)) {
            alreadyIn = true;
            break;
          }
        }
        if (!alreadyIn) map[pid].push({ _id: v._id, name: v.name });
      }
    }
    return map;
  }

  // Apply search + assigned-filter + sort. Selection is independent of
  // the visible filter so the user can stage a bulk assignment across
  // multiple filter views.
  filteredAndSorted() {
    var q          = this.state.qInput.trim().toLowerCase();
    var byPatient  = this.verifiersByPatient();
    var filterMode = this.state.assignFilter;

    var rows = (this.state.patients || []).filter(function (p) {
      // Exclude internal Device Testing rows (matches patient-list behavior)
      if (p.doctorName === "Device Testing") return false;

      // assigned / unassigned filter
      if (filterMode === "assigned"  && !(byPatient[String(p._id)] || []).length) return false;
      if (filterMode === "unassigned" && (byPatient[String(p._id)] || []).length) return false;

      // search
      if (!q) return true;
      var name = String(p.name         || "").toLowerCase();
      var dev  = String(p.device_id    || "").toLowerCase();
      var pid  = String(p._id          || "").toLowerCase();
      var cust = String(p.customerName || "").toLowerCase();
      return name.indexOf(q) !== -1 || dev.indexOf(q) !== -1
          || pid.indexOf(q)  !== -1 || cust.indexOf(q) !== -1;
    });

    // Pick the "confirm / upload" timestamp from whichever field carries
    // it on this patient. ConfirmupdatedDate fires when the partner ticks
    // "confirm for verification"; updatedDate is the device's last data
    // upload; created_at is the registration moment. Newer = closer to
    // the top of the verifier queue.
    function confirmTs(p) {
      var t = p && (p.ConfirmupdatedDate || p.updatedDate || p.created_at);
      if (!t) return 0;
      var ms = new Date(t).getTime();
      return isNaN(ms) ? 0 : ms;
    }

    var k = this.state.sortKey;
    rows = rows.slice().sort(function (a, b) {
      if (k === "confirmDate") {
        // OLDEST confirmed/uploaded first — assigner rule-1 default.
        var at = confirmTs(a);
        var bt = confirmTs(b);
        if (at !== bt) return at - bt;
      } else if (k === "confirmDateNewest") {
        // NEWEST confirmed/uploaded first — when the assigner wants
        // to see fresh uploads at the top instead of the queue order.
        var atN = confirmTs(a);
        var btN = confirmTs(b);
        if (atN !== btN) return btN - atN;
      } else if (k === "name") {
        var an = String(a.name || "").toLowerCase();
        var bn = String(b.name || "").toLowerCase();
        if (an !== bn) return an < bn ? -1 : 1;
      } else if (k === "id") {
        var ai = String(a.device_id || "");
        var bi = String(b.device_id || "");
        if (ai !== bi) return ai < bi ? -1 : 1;
      } else if (k === "createdAt") {
        var ac = a.created_at ? new Date(a.created_at).getTime() : 0;
        var bc = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (ac !== bc) return bc - ac;       // newest first
      }
      return String(a._id).localeCompare(String(b._id));
    });
    return rows;
  }

  setPage = (next, totalPages) => {
    if (next < 1) next = 1;
    if (next > totalPages) next = totalPages;
    if (next === this.state.page) return;
    this.setState({ page: next });
    try {
      var w = document.querySelector(".ap-table-wrap");
      if (w) w.scrollTop = 0;
    } catch (e) { /* ignore */ }
  };

  toggleRow = (pid) => {
    var s = new Set(this.state.selected);
    if (s.has(pid)) s.delete(pid); else s.add(pid);
    this.setState({ selected: s });
  };

  // Master checkbox in the header — toggles every CURRENTLY visible
  // (filtered + paginated) row.
  toggleAllOnPage = (visibleIds) => {
    var s = new Set(this.state.selected);
    var allChecked = visibleIds.every(function (id) { return s.has(id); });
    if (allChecked) {
      // un-check the visible page
      visibleIds.forEach(function (id) { s.delete(id); });
    } else {
      visibleIds.forEach(function (id) { s.add(id); });
    }
    this.setState({ selected: s });
  };

  clearSelection = () => this.setState({ selected: new Set() });

  // Send the bulk assignment to the backend.
  apply = async () => {
    var sel    = Array.from(this.state.selected);
    var target = this.state.targetVerifier;
    if (!sel.length)   { this.setState({ error: "Pick at least one patient." }); return; }
    if (!target)       { this.setState({ error: "Pick a verifier to assign to." }); return; }

    this.setState({ saving: true, error: "", flash: "" });
    try {
      var res = await axios.post(apiUrl + "api/users/bulkAssignVerifier", {
        patientIds:  sel,
        verifierIds: [target],
        mode:        this.state.mode,     // "add" or "replace"
      });
      var data = res.data || {};
      this.setState({
        saving: false,
        flash:
          (data.message || "Updated")
          + " — " + sel.length + " patient" + (sel.length === 1 ? "" : "s")
          + " (mode: " + this.state.mode + ")",
        selected: new Set(),
      });
      // Refresh both lists so currently-assigned verifier names update
      this.load();
    } catch (err) {
      this.setState({
        saving: false,
        error:
          (err.response && err.response.data && err.response.data.error) ||
          err.message || "Save failed",
      });
    }
  };

  handleLogout = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    await performLogout();
  };

  // ── Quick-assigner notes ───────────────────────────────────────────
  // Update the textarea + mirror to localStorage so the paste survives
  // refreshes. Called on every keystroke.
  onNotesChange = (e) => {
    var txt = e.target.value;
    this.setState({ notes: txt });
    try { localStorage.setItem(NOTES_LS_KEY, txt); } catch (err) { /* ignore */ }
  };

  // Wipe the textarea + LS, and reset the auto-fill flag so the next
  // page load (or the next Build-from-list click) repopulates from the
  // patient list.
  clearNotes = () => {
    this._notesAutoFilled = false;
    this.setState({ notes: "", notesFlash: "" });
    try { localStorage.removeItem(NOTES_LS_KEY); } catch (err) { /* ignore */ }
  };

  // Explicit "build from current patients" — the primary way to refresh
  // the notes to match what's currently visible in the table. Always
  // regenerates, regardless of the auto-fill flag.
  buildNotesNow = () => {
    var assigners = this.state.notesAssigners || [];
    if (!assigners.length) {
      this.setState({
        notesFlash: "",
        error: "Pick at least one assigner in the \"Available assigners\" row above — click a pill to add it (turns teal). Then click Build again."
      });
      return;
    }
    // No ticking required: by default the list is prepared from ALL
    // pending patients, ordered by upload time (oldest first) inside
    // the skill engine. Ticking rows narrows the batch to exactly the
    // selected patients.
    var sel          = this.state.selected;
    var useSelection = !!(sel && sel.size);
    var pool         = this.state.patients || [];
    if (useSelection) {
      pool = pool.filter(function (p) { return p && sel.has(String(p._id)); });
    }
    var generated = buildDistributedNotes(
      pool,
      assigners,
      this.state.verifiers,
      true // always use the whole pool — upload-time order decides
    );
    if (!generated) {
      this.setState({
        error: useSelection
          ? "None of the selected patients are assignable — they're delivered, running, or on hold."
          : "No assignable patients — everything pending is delivered, running, or on hold.",
      });
      return;
    }
    this._notesAutoFilled = true;
    var flash = useSelection
      ? "Notes built from " + pool.length + " selected patient" + (pool.length === 1 ? "" : "s")
        + " · sets dealt across " + assigners.length
        + " verifier" + (assigners.length === 1 ? "" : "s") + "."
      : "Notes built from all pending patients (oldest upload first) · sets dealt across "
        + assigners.length + " verifier" + (assigners.length === 1 ? "" : "s") + ".";
    this.setState({ notes: generated, notesFlash: flash, error: "" });
    try { localStorage.setItem(NOTES_LS_KEY, generated); } catch (err) { /* ignore */ }
  };

  // Toggle a verifier in/out of the selected assigners list.
  // Triggered by clicking any pill in the assigner-list row.
  toggleNotesAssigner = (vid) => {
    if (!vid) return;
    var cur = this.state.notesAssigners || [];
    var idx = cur.indexOf(vid);
    if (idx === -1) {
      this.setState({ notesAssigners: cur.concat([vid]) });
    } else {
      this.setState({
        notesAssigners: cur.filter(function (id) { return id !== vid; }),
      });
    }
  };

  // Quick-clear the whole assigner selection (header link).
  clearNotesAssigners = () => {
    this.setState({ notesAssigners: [] });
  };

  // Parse the notes textarea and dispatch assignments. Two modes:
  //
  //   • Distributed — the paste has one section per verifier with a
  //     name (or "verifier N") header. Each section's device IDs go
  //     ONLY to that section's verifier. One backend call per section.
  //
  //   • Batch — the paste is a flat list of device IDs (no headers).
  //     Every parsed ID goes to every selected assigner pill.
  applyNotes = async () => {
    var sections = parseDistributedNotes(
      this.state.notes,
      this.state.notesAssigners || [],
      this.state.verifiers || []
    );

    if (sections.length) return this.applyDistributedNotes(sections);
    return this.applyBatchNotes();
  };

  // ── Distributed apply — one bulkAssignVerifier call per section. ──
  applyDistributedNotes = async (sections) => {
    // Build device_id → patient._id lookup once.
    var byDev = {};
    var ps = this.state.patients || [];
    for (var i = 0; i < ps.length; i++) {
      var d = String(ps[i].device_id || "");
      if (d) byDev[d] = String(ps[i]._id);
    }

    // Build the per-section plan.
    var plan = [];
    var allUnmatched = [];
    sections.forEach(function (s) {
      var matched = [];
      s.deviceIds.forEach(function (d) {
        if (byDev[d]) matched.push(byDev[d]); else allUnmatched.push(d);
      });
      if (matched.length) {
        plan.push({
          verifierId:   s.verifierId,
          verifierName: s.verifierName,
          patientIds:   matched,
        });
      }
    });

    if (!plan.length) {
      this.setState({
        notesFlash: "",
        error:      "None of the device IDs in the notes match patients on this page.",
      });
      return;
    }

    this.setState({ notesApplying: true, error: "", notesFlash: "" });
    try {
      var summary = [];
      var total   = 0;
      for (var p = 0; p < plan.length; p++) {
        var step = plan[p];
        // eslint-disable-next-line no-await-in-loop
        await axios.post(apiUrl + "api/users/bulkAssignVerifier", {
          patientIds:  step.patientIds,
          verifierIds: [step.verifierId],
          mode:        this.state.notesMode,
        });
        summary.push(step.verifierName + " ← " + step.patientIds.length);
        total += step.patientIds.length;
      }

      var msg = total + " assignment" + (total === 1 ? "" : "s")
              + " across " + plan.length + " verifier" + (plan.length === 1 ? "" : "s")
              + " (" + summary.join(", ") + ", mode: " + this.state.notesMode + ")";
      if (allUnmatched.length) {
        msg += " · " + allUnmatched.length + " unmatched: "
            + allUnmatched.slice(0, 6).join(", ")
            + (allUnmatched.length > 6 ? "…" : "");
      }
      this.setState({ notesApplying: false, notesFlash: msg });
      this.load();
    } catch (err) {
      this.setState({
        notesApplying: false,
        error:
          (err.response && err.response.data && err.response.data.error) ||
          err.message || "Save failed",
      });
    }
  };

  // ── Batch apply — flat list, all parsed IDs to all picked pills. ──
  applyBatchNotes = async () => {
    var ids = extractDeviceIds(this.state.notes);
    if (!ids.length) {
      this.setState({ notesFlash: "", error: "No device IDs found in notes." });
      return;
    }
    var assigners = this.state.notesAssigners || [];
    if (!assigners.length) {
      this.setState({
        notesFlash: "",
        error: "No verifier headers found in notes AND no assigner pills picked — pick an assigner or add headers."
      });
      return;
    }

    // device_id → patient._id lookup. Patients carry device_id as a
    // number; coerce to string for comparison so "245876" matches both.
    var byDev = {};
    var ps = this.state.patients || [];
    for (var i = 0; i < ps.length; i++) {
      var d = String(ps[i].device_id || "");
      if (d) byDev[d] = String(ps[i]._id);
    }

    var matched   = [];
    var unmatched = [];
    for (var j = 0; j < ids.length; j++) {
      var pid = byDev[ids[j]];
      if (pid) matched.push(pid); else unmatched.push(ids[j]);
    }

    if (!matched.length) {
      this.setState({
        notesFlash: "",
        error: "None of the " + ids.length + " device IDs in notes match a patient on this page.",
      });
      return;
    }

    this.setState({ notesApplying: true, error: "", notesFlash: "" });
    try {
      var res = await axios.post(apiUrl + "api/users/bulkAssignVerifier", {
        patientIds:  matched,
        verifierIds: assigners,
        mode:        this.state.notesMode,
      });
      var data = res.data || {};

      // Friendly names for the flash banner
      var vmap = {};
      (this.state.verifiers || []).forEach(function (x) {
        vmap[String(x._id)] = x.name;
      });
      var vnames = assigners.map(function (id) { return vmap[id] || "verifier"; }).join(", ");

      var msg = (data.message || "Assigned")
        + ": " + matched.length + " patient" + (matched.length === 1 ? "" : "s")
        + " → " + vnames
        + " (mode: " + this.state.notesMode + ")";
      if (unmatched.length) {
        msg += " · " + unmatched.length + " unmatched: "
            + unmatched.slice(0, 6).join(", ")
            + (unmatched.length > 6 ? "…" : "");
      }
      this.setState({ notesApplying: false, notesFlash: msg });
      this.load();   // refresh so Assigned verifier(s) col updates
    } catch (err) {
      this.setState({
        notesApplying: false,
        error:
          (err.response && err.response.data && err.response.data.error) ||
          err.message || "Save failed",
      });
    }
  };

  render() {
    if (this.state.error && !this.state.patients.length) {
      return (
        <div className="ap-root">
          <SideBar
            menus={[
              { title: "Patient List",       url: "/patient-list" },
              { title: "Verifier Home",      url: "/verifier-home" },
              { title: "Analysis Dashboard", url: "/analysis-dashboard" },
              { title: "Users by Role",      url: "/users-by-role" },
              { title: "Telemetry",          url: "/telemetry" },
              { title: "Assign Patients",    url: "/assign-patients" },
              { title: "Verified",           url: "/Verification-Completed" },
              { title: "QC",                 url: "/qc_home" },
            ]}
            profileLink="verifier-profile"
            hideProfile={true}
            hideLogout={true}
          />
          <div className="ap-page">
            <div className="ap-error">{this.state.error}</div>
          </div>
        </div>
      );
    }

    var allRows    = this.filteredAndSorted();
    var totalRows  = allRows.length;
    var totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
    var page       = Math.min(this.state.page, totalPages);
    var first      = (page - 1) * PAGE_SIZE;
    var last       = Math.min(first + PAGE_SIZE, totalRows);
    var rows       = allRows.slice(first, last);
    var pageNums   = pageWindow(page, totalPages);
    var byPatient  = this.verifiersByPatient();

    var visibleIds   = rows.map(function (r) { return String(r._id); });
    var allVisibleOn = visibleIds.length > 0
      && visibleIds.every((id) => this.state.selected.has(id));

    return (
      <div className="ap-root">
        <SideBar
          menus={[
              { title: "Patient List",       url: "/patient-list" },
              { title: "Verifier Home",      url: "/verifier-home" },
              { title: "Analysis Dashboard", url: "/analysis-dashboard" },
              { title: "Users by Role",      url: "/users-by-role" },
              { title: "Telemetry",          url: "/telemetry" },
              { title: "Assign Patients",    url: "/assign-patients" },
              { title: "Verified",           url: "/Verification-Completed" },
              { title: "QC",                 url: "/qc_home" },
            ]}
          profileLink="verifier-profile"
          hideProfile={true}
          hideLogout={true}
        />

        <div className="ap-page">
          {/* ── Header ────────────────────────────────────────── */}
          <div className="ap-header">
            <div>
              <h2>Verifier patient assignment</h2>
              <span className="ap-sub">
                {totalRows} patient{totalRows === 1 ? "" : "s"} visible ·{" "}
                <b>{this.state.selected.size}</b> selected
                {this.state.lastUpdated && (
                  <>
                    {" · "}
                    <span className="ap-sub__live" title={"Auto-refresh every " + Math.round(this.AUTO_REFRESH_MS / 1000) + "s"}>
                      ● live
                    </span>
                    {" "}
                    <span style={{ opacity: 0.7 }}>
                      updated {this.state.lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </span>
                  </>
                )}
              </span>
            </div>
            <div className="ap-header__actions">
              <button
                className="ap-btn ap-btn--ghost"
                onClick={this.load}
                disabled={this.state.loading}
              >
                {this.state.loading ? "Refreshing…" : "↻ Refresh"}
              </button>
              <button
                className="ap-btn ap-btn--logout"
                onClick={this.handleLogout}
                title="Sign out"
              >
                🚪 Logout
              </button>
            </div>
          </div>

          {/* ── Quick-assigner notes panel ────────────────────── */}
          {/* Always visible — pick assigner(s), paste device IDs, Apply.
              Survives reloads via localStorage. */}
          <div className="ap-notes">
            <div className="ap-notes__head">
              <span className="ap-notes__title">📋 Quick assigner notes</span>
              <span className="ap-notes__hint">
                {extractDeviceIds(this.state.notes).length} device ID
                {extractDeviceIds(this.state.notes).length === 1 ? "" : "s"} parsed
                {" · "}
                <b>{(this.state.notesAssigners || []).length}</b> assigner
                {(this.state.notesAssigners || []).length === 1 ? "" : "s"} selected
              </span>
            </div>

            {/* ── Assigner list — always visible — click pills to toggle ── */}
            <div className="ap-notes__pillsrow">
              <div className="ap-notes__pillsLabel">
                <span>Available assigners</span>
                <span className="ap-notes__pillsLabel--muted">
                  — click pills to add (turns teal). Build needs ≥1 picked.
                </span>
                {(this.state.notesAssigners || []).length > 0 && (
                  <button
                    type="button"
                    className="ap-link"
                    onClick={this.clearNotesAssigners}
                  >clear all</button>
                )}
              </div>
              <div className="ap-notes__pills">
                {(this.state.verifiers || []).length === 0 && (
                  <span className="ap-notes__pillsLabel ap-notes__pillsLabel--muted">
                    Loading verifier list…
                  </span>
                )}
                {(this.state.verifiers || []).map((v) => {
                  var picked = (this.state.notesAssigners || []).indexOf(v._id) !== -1;
                  return (
                    <button
                      type="button"
                      key={v._id}
                      className={
                        "ap-pill" + (picked ? " ap-pill--on" : "")
                      }
                      onClick={() => this.toggleNotesAssigner(v._id)}
                      title={picked ? "Click to remove" : "Click to add as assigner"}
                    >
                      {picked && <span className="ap-pill__check">✓</span>}
                      {v.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <textarea
              className="ap-notes__ta"
              rows={8}
              value={this.state.notes}
              onChange={this.onNotesChange}
              placeholder="Paste device IDs here — e.g. &quot;246087 first hlf&quot; — any annotation is allowed, only digits are extracted."
            />

            <div className="ap-notes__row">
              <select
                className="ap-search ap-search--narrow"
                value={this.state.notesMode}
                onChange={(e) => this.setState({ notesMode: e.target.value })}
                title={
                  this.state.notesMode === "add"
                    ? "Adds to existing verifier(s) on the patient"
                    : "Removes all other verifiers, sets only these"
                }
              >
                <option value="add">Mode: Add (keep existing)</option>
                <option value="replace">Mode: Replace (clear others)</option>
              </select>
              <button
                className="ap-btn ap-btn--primary"
                onClick={this.applyNotes}
                disabled={this.state.notesApplying}
              >
                {this.state.notesApplying ? "Applying…" : "✓ Apply notes"}
              </button>
              <button
                className="ap-btn ap-btn--ghost"
                onClick={this.clearNotes}
                disabled={!this.state.notes}
                title="Clear textarea (does not undo assignments)"
              >
                ✕ Clear notes
              </button>
              <button
                className="ap-btn ap-btn--ghost"
                onClick={this.buildNotesNow}
                disabled={
                  !(this.state.patients || []).length
                  || !(this.state.notesAssigners || []).length
                }
                title={
                  !(this.state.notesAssigners || []).length
                    ? "Pick at least one assigner pill above before clicking Build"
                    : "Generate verifier-distributed notes from the current patient list"
                }
              >
                ✨ Build for {(this.state.notesAssigners || []).length || 0} verifier
                {(this.state.notesAssigners || []).length === 1 ? "" : "s"}
              </button>
            </div>
            {this.state.notesFlash && (
              <div className="ap-notes__flash">✓ {this.state.notesFlash}</div>
            )}
          </div>

          {/* ── Flash + error ─────────────────────────────────── */}
          {this.state.flash && (
            <div className="ap-flash">✓ {this.state.flash}</div>
          )}
          {this.state.error && (
            <div className="ap-error">{this.state.error}</div>
          )}

          {/* ── Filters row ───────────────────────────────────── */}
          <div className="ap-filters">
            <input
              className="ap-search"
              type="text"
              placeholder="Search patient name, device id, patient id, customer…"
              value={this.state.qInput}
              onChange={(e) => this.setState({ qInput: e.target.value, page: 1 })}
            />
            <select
              className="ap-search ap-search--narrow"
              value={this.state.assignFilter}
              onChange={(e) => this.setState({ assignFilter: e.target.value, page: 1 })}
            >
              <option value="all">All patients</option>
              <option value="assigned">Already assigned</option>
              <option value="unassigned">Unassigned only</option>
            </select>
            <select
              className="ap-search ap-search--narrow"
              value={this.state.sortKey}
              onChange={(e) => this.setState({ sortKey: e.target.value, page: 1 })}
            >
              <option value="confirmDate">Sort: Oldest confirmed / uploaded (default)</option>
              <option value="confirmDateNewest">Sort: Newest confirmed / uploaded</option>
              <option value="createdAt">Sort: Newest registered</option>
              <option value="name">Sort: Name (A–Z)</option>
              <option value="id">Sort: Device id</option>
            </select>
          </div>

          {/* ── Bulk-action toolbar ───────────────────────────── */}
          <div className="ap-bulk">
            <div className="ap-bulk__count">
              <b>{this.state.selected.size}</b> selected
              {this.state.selected.size > 0 && (
                <button
                  className="ap-link"
                  onClick={this.clearSelection}
                  type="button"
                >clear</button>
              )}
            </div>

            <select
              className="ap-search ap-search--narrow"
              value={this.state.targetVerifier}
              onChange={(e) => this.setState({ targetVerifier: e.target.value })}
            >
              <option value="">Assign to verifier…</option>
              {(this.state.verifiers || []).map((v) => (
                <option key={v._id} value={v._id}>{v.name}</option>
              ))}
            </select>

            <select
              className="ap-search ap-search--narrow"
              value={this.state.mode}
              onChange={(e) => this.setState({ mode: e.target.value })}
              title={
                this.state.mode === "add"
                  ? "Adds to existing verifier(s) on the patient"
                  : "Removes all other verifiers, sets only this one"
              }
            >
              <option value="add">Mode: Add (keep existing)</option>
              <option value="replace">Mode: Replace (clear others)</option>
            </select>

            <button
              className="ap-btn ap-btn--primary"
              onClick={this.apply}
              disabled={
                this.state.saving
                || !this.state.selected.size
                || !this.state.targetVerifier
              }
            >
              {this.state.saving ? "Saving…" : "Apply assignment"}
            </button>
          </div>

          {/* ── Table ─────────────────────────────────────────── */}
          <div className="ap-table-wrap">
            <table className="ap-table">
              <colgroup>
                <col className="ap-col-sel" />
                <col className="ap-col-sl" />
                <col className="ap-col-name" />
                <col className="ap-col-pid" />
                <col className="ap-col-regdate" />
                <col className="ap-col-customer" />
                <col className="ap-col-duration" />
                <col className="ap-col-doctor" />
                <col className="ap-col-verifier" />
              </colgroup>
              <thead>
                <tr>
                  <th className="ap-col-sel">
                    <input
                      type="checkbox"
                      checked={allVisibleOn}
                      onChange={() => this.toggleAllOnPage(visibleIds)}
                      title="Select / clear this page"
                    />
                  </th>
                  <th className="ap-col-sl">SL</th>
                  <th className="ap-col-name">Patient name</th>
                  <th className="ap-col-pid">Patient ID</th>
                  <th className="ap-col-regdate">Confirmed / uploaded</th>
                  <th className="ap-col-customer">Customer</th>
                  <th className="ap-col-duration">Monitoring</th>
                  <th className="ap-col-doctor">Referring doctor</th>
                  <th className="ap-col-verifier">Assigned verifier(s)</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !this.state.loading && (
                  <tr>
                    <td colSpan="9" className="ap-empty">
                      No patients match these filters.
                    </td>
                  </tr>
                )}
                {rows.map((r, idx) => {
                  var pid     = String(r._id);
                  var checked = this.state.selected.has(pid);
                  var vs      = byPatient[pid] || [];
                  return (
                    <tr key={pid} className={checked ? "ap-row--checked" : ""}>
                      <td className="ap-col-sel">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => this.toggleRow(pid)}
                        />
                      </td>
                      <td className="ap-col-sl">{first + idx + 1}</td>
                      <td className="ap-col-name ap-name">
                        {r.name || "—"}
                        {isPaced(r) && (
                          <span
                            className="ap-tag"
                            title="Pacemaker patient — route to a senior verifier (rule 5)"
                            style={{ marginLeft: 6, background: "#fff4e0", color: "#a35a00", borderColor: "#f0c280" }}
                          >paced</span>
                        )}
                      </td>
                      <td className="ap-col-pid ap-mono" title={pid}>
                        {r.device_id || "—"}
                      </td>
                      <td className="ap-col-regdate">
                        {(function () {
                          // Same field-priority as the sort: ConfirmupdatedDate
                          // (partner confirmed) > updatedDate (device upload)
                          // > created_at (registration). Show whichever exists.
                          var t = r.ConfirmupdatedDate || r.updatedDate || r.created_at;
                          if (!t) return "—";
                          var d = new Date(t);
                          if (isNaN(d.getTime())) return String(t);
                          return d.toLocaleDateString() + " " +
                                 d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                        })()}
                      </td>
                      <td className="ap-col-customer">
                        {r.customerName || "—"}
                        {r.customerName && !matchDistributor(r.customerName) && (
                          <span
                            title={
                              "Not in known distributor list — confirm before assigning. " +
                              "Known: " + KNOWN_DISTRIBUTORS.join(", ")
                            }
                            style={{
                              marginLeft: 6,
                              color: "#a35a00",
                              cursor: "help",
                              fontSize: "0.85em",
                            }}
                          >⚠</span>
                        )}
                      </td>
                      <td className="ap-col-duration">
                        {(function () {
                          // Normalize 24/48/72 → 1/2/3 (see helper at top
                          // of file). Falls back to the raw value when
                          // normalization can't infer days (so we never
                          // silently hide a bad-data row).
                          var normalized = normalizeMonitoringDays(r.exp_device_use_days);
                          if (normalized) return normalized + "d";
                          if (r.exp_device_use_days === undefined
                              || r.exp_device_use_days === null
                              || r.exp_device_use_days === "") return "—";
                          // un-normalizable — surface as-is with a marker
                          return String(r.exp_device_use_days) + "?";
                        })()}
                      </td>
                      <td className="ap-col-doctor">
                        {r.doctorName || "—"}
                      </td>
                      <td className="ap-col-verifier">
                        {vs.length === 0 ? (
                          <span className="ap-tag ap-tag--muted">Unassigned</span>
                        ) : (
                          (function () {
                            // Cap to MAX_TAGS_PER_ROW so rows stay
                            // compact even when a patient is on every
                            // verifier's list. "+N more" tooltip shows
                            // the full list on hover.
                            var MAX = 4;
                            var head = vs.slice(0, MAX);
                            var tail = vs.slice(MAX);
                            return (
                              <>
                                {head.map((v) => (
                                  <span key={v._id} className="ap-tag">
                                    {v.name}
                                  </span>
                                ))}
                                {tail.length > 0 && (
                                  <span
                                    className="ap-tag ap-tag--more"
                                    title={tail.map((v) => v.name).join(", ")}
                                  >+{tail.length} more</span>
                                )}
                              </>
                            );
                          })()
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pager ─────────────────────────────────────────── */}
          {totalRows > 0 && (
            <div className="ap-pager">
              <div className="ap-pager__info">
                Showing <b>{first + 1}</b>–<b>{last}</b> of <b>{totalRows}</b>
              </div>
              <div className="ap-pager__controls">
                <button
                  className="ap-pager__btn"
                  onClick={() => this.setPage(page - 1, totalPages)}
                  disabled={page === 1}
                >‹ Prev</button>
                {pageNums.map(function (p, i) {
                  if (p === "…") {
                    return <span key={"gap-" + i} className="ap-pager__gap">…</span>;
                  }
                  return (
                    <button
                      key={p}
                      className={
                        "ap-pager__btn" +
                        (p === page ? " ap-pager__btn--active" : "")
                      }
                      onClick={() => this.setPage(p, totalPages)}
                    >{p}</button>
                  );
                }.bind(this))}
                <button
                  className="ap-pager__btn"
                  onClick={() => this.setPage(page + 1, totalPages)}
                  disabled={page === totalPages}
                >Next ›</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
